import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  Grid2,
  IconButton,
  Paper,
  TextField,
  useTheme,
} from "@mui/material";
import { observer, useLocalObservable } from "mobx-react";
import { runInAction } from "mobx";
import React, { useEffect, useRef } from "react";
import { Product } from "../../../../../models/Products/Product";
import {
  ProductVariantApi,
  UpdateVariantsInput,
  getApiErrorMessage,
} from "../../../../../models/CatalogProducts/types";
import { OrderProductsVariantSchema } from "../../../../../models/Order/OrderProductsVariant";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import { OrderProductVariantsModuleStore } from "../../../stores/OrderProductVariantsModuleStore";
import { useStores } from "../../../use-stores";
import { useDebounce } from "../../../../../views/components/common/hooks/Debounce";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import SkuCopyRow from "../../../../../views/components/common/SkuCopyRow";
import ProductSkuBuilder from "../../pages/products/ProductSkuBuilder";

export interface ProductVariantRowInput {
  title: string;
  quantity: number;
  sku?: string;
}

export interface VariantApiPayload {
  product_id: number | string | null;
  title: string | null;
  quantity: number;
  sku: string | null;
}

interface OrderProductVariantsDrawerProps {
  open: boolean;
  product_id?: number | string | null;
  order_id?: number | string | null;
  total_quantity: number;
  productDetails?: Product | null;
  isLoading?: boolean;
  initialRows?: ProductVariantRowInput[];
  onClose: () => void;
  onSubmit: (rows: ProductVariantRowInput[]) => void;
  onSaveVariant?: (payload: VariantApiPayload) => void;
  onDeleteVariant?: (payload: VariantApiPayload) => void;
  /** Fired whenever the linked order-variant SKU count changes (fetch/save/delete). */
  onVariantsCountChange?: (count: number) => void;
}

type Step = "order" | "catalog";
type CatalogTab = "list" | "create";

type SelectedCatalogDraft = {
  catalogId: number;
  quantity: string;
};

const VARIANT_DRAWER_Z_OFFSET = 2000;

function catalogLabel(v: ProductVariantApi): string {
  return (v.title || v.name || "Untitled variant").trim();
}

function mapCatalogToOrderPayload(
  productId: number,
  catalog: ProductVariantApi,
  quantity: number,
) {
  return {
    product_id: productId,
    sku: (catalog.sku || `CAT-${catalog.id}`).trim(),
    quantity,
  };
}

function toParentPayload(
  productId: number | string | null | undefined,
  title: string | null,
  quantity: number,
  sku: string | null,
): VariantApiPayload {
  return {
    product_id: productId ?? null,
    title,
    quantity,
    sku,
  };
}

const OrderProductVariantsDrawer: React.FC<OrderProductVariantsDrawerProps> = ({
  open,
  product_id,
  order_id,
  total_quantity,
  isLoading = false,
  onClose,
  onSaveVariant,
  onDeleteVariant,
  onVariantsCountChange,
}) => {
  const theme = useTheme();
  const colors = theme.palette.customColors;
  const { userStore, orderStore } = useStores();
  const moduleStore = useRef(new OrderProductVariantsModuleStore()).current;

  const page = useLocalObservable(() => ({
    step: "order" as Step,
    catalogTab: "list" as CatalogTab,
    catalogSearch: "",
    selectedCatalogDrafts: [] as SelectedCatalogDraft[],
    editingOrderVariantId: null as number | null,
    editQuantity: "",
    pendingNewVariants: [] as UpdateVariantsInput["variants"],
    skuBuilderResetKey: 0,
    saving: false,
    deletingId: null as number | null,
    creatingCatalog: false,
    bootstrapError: null as string | null,
    actionError: null as string | null,
    productName: "",
    categoryId: null as number | null,
    productSku: null as string | null,
    setStep(step: Step) {
      this.step = step;
      this.actionError = null;
      if (step === "catalog") {
        this.catalogTab = "list";
        this.selectedCatalogDrafts = [];
        this.pendingNewVariants = [];
      }
    },
    setCatalogTab(tab: CatalogTab) {
      this.catalogTab = tab;
      this.actionError = null;
      if (tab === "create") {
        this.selectedCatalogDrafts = [];
      } else {
        this.pendingNewVariants = [];
      }
    },
    setCatalogSearch(value: string) {
      this.catalogSearch = value;
    },
    toggleCatalogVariant(id: number) {
      const existingIndex = this.selectedCatalogDrafts.findIndex(
        (draft) => draft.catalogId === id,
      );
      if (existingIndex >= 0) {
        this.selectedCatalogDrafts = this.selectedCatalogDrafts.filter(
          (draft) => draft.catalogId !== id,
        );
      } else {
        this.selectedCatalogDrafts = [
          ...this.selectedCatalogDrafts,
          { catalogId: id, quantity: "1" },
        ];
      }
      this.actionError = null;
    },
    setSelectedCatalogQuantity(catalogId: number, quantity: string) {
      const draft = this.selectedCatalogDrafts.find(
        (item) => item.catalogId === catalogId,
      );
      if (draft) {
        draft.quantity = quantity;
      }
    },
    removeSelectedCatalog(catalogId: number) {
      this.selectedCatalogDrafts = this.selectedCatalogDrafts.filter(
        (draft) => draft.catalogId !== catalogId,
      );
    },
    setSelectedCatalogDrafts(drafts: SelectedCatalogDraft[]) {
      this.selectedCatalogDrafts = drafts;
    },
  }));

  const debouncedCatalogSearch = useDebounce(page.catalogSearch, 400);

  const hasEditPermissions = (): boolean => {
    if (orderStore.orderBasicDetails?.project_id) {
      return userStore.hasPermission([
        EmployeeRoles.EDIT_ORDER_AFTER_PROJECT_CREATION,
      ]);
    }
    return userStore.hasPermission([
      EmployeeRoles.CREATE_ORDER,
      EmployeeRoles.EDIT_ORDER,
    ]);
  };

  const canEdit = hasEditPermissions();
  const canCreateVariantsOrOptions = userStore.hasPermission(
    EmployeeRoles.EDIT_CATALOG,
    true,
  );

  const notifyVariantsCount = () => {
    onVariantsCountChange?.(moduleStore.orderVariants.length);
  };

  const bootstrap = async () => {
    if (!product_id || !order_id) return;
    try {
      page.bootstrapError = null;
      const detail = await moduleStore.loadProductDetail(Number(product_id));
      runInAction(() => {
        page.productName = detail?.name ?? "";
        page.categoryId = detail?.category_id ?? null;
        page.productSku = detail?.sku ?? null;
      });
      await Promise.all([
        moduleStore.loadOrderVariants(Number(order_id), Number(product_id)),
        moduleStore.loadCatalogVariants(Number(product_id)),
      ]);
      notifyVariantsCount();
    } catch (error) {
      runInAction(() => {
        page.bootstrapError = getApiErrorMessage(
          error,
          "Failed to load variants.",
        );
      });
    }
  };

  useEffect(() => {
    if (!open) return;
    runInAction(() => {
      page.step = "order";
      page.catalogTab = "list";
      page.catalogSearch = "";
      page.selectedCatalogDrafts = [];
      page.pendingNewVariants = [];
      page.editingOrderVariantId = null;
      page.actionError = null;
    });
    void bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, product_id, order_id]);

  const filteredCatalog = moduleStore.catalogVariants.filter((v) => {
    const term = debouncedCatalogSearch.trim().toLowerCase();
    if (!term) return true;
    const title = catalogLabel(v).toLowerCase();
    const sku = (v.sku || "").toLowerCase();
    const name = (v.name || "").toLowerCase();
    return title.includes(term) || sku.includes(term) || name.includes(term);
  });

  const alreadyLinkedSku = new Set(
    moduleStore.orderVariants.map((v) => (v.sku || "").trim().toLowerCase()),
  );

  const handleSaveSelectedCatalog = async () => {
    if (
      !canEdit ||
      !product_id ||
      !order_id ||
      page.selectedCatalogDrafts.length === 0
    ) {
      return;
    }

    const payloads: Array<{
      catalog: ProductVariantApi;
      quantity: number;
      sku: string;
    }> = [];
    const selectedSkus = new Set<string>();

    for (const draft of page.selectedCatalogDrafts) {
      const catalog = moduleStore.catalogVariants.find(
        (v) => v.id === draft.catalogId,
      );
      if (!catalog) {
        page.actionError = "One or more selected catalog variants are missing.";
        return;
      }

      const qty = parseInt(draft.quantity, 10);
      if (!Number.isFinite(qty) || qty <= 0) {
        page.actionError = `Enter a valid quantity for ${catalogLabel(catalog)} (minimum 1).`;
        return;
      }

      const sku = (catalog.sku || `CAT-${catalog.id}`).trim();
      const normalizedSku = sku.toLowerCase();

      if (alreadyLinkedSku.has(normalizedSku)) {
        page.actionError = `${catalogLabel(catalog)} is already linked on this order product.`;
        return;
      }

      if (selectedSkus.has(normalizedSku)) {
        page.actionError = `${catalogLabel(catalog)} is selected more than once.`;
        return;
      }

      selectedSkus.add(normalizedSku);
      payloads.push({ catalog, quantity: qty, sku });
    }

    try {
      page.saving = true;
      page.actionError = null;

      for (const item of payloads) {
        const payload = mapCatalogToOrderPayload(
          Number(product_id),
          item.catalog,
          item.quantity,
        );
        await moduleStore.saveOrderVariant(Number(order_id), payload);
        onSaveVariant?.(
          toParentPayload(
            product_id,
            catalogLabel(item.catalog),
            payload.quantity,
            payload.sku,
          ),
        );
      }

      await moduleStore.loadOrderVariants(Number(order_id), Number(product_id));
      notifyVariantsCount();
      runInAction(() => {
        page.selectedCatalogDrafts = [];
      });
    } catch (error) {
      runInAction(() => {
        page.actionError = getApiErrorMessage(
          error,
          "Could not save order variant(s).",
        );
      });
    } finally {
      runInAction(() => {
        page.saving = false;
      });
    }
  };

  const handleCreateCatalogVariants = async () => {
    if (!canEdit || !product_id || page.pendingNewVariants.length === 0) return;
    try {
      page.creatingCatalog = true;
      page.actionError = null;
      const created = await moduleStore.createCatalogVariants(
        Number(product_id),
        page.pendingNewVariants,
      );
      runInAction(() => {
        page.pendingNewVariants = [];
        page.skuBuilderResetKey += 1;
        page.catalogTab = "list";
        if (created.length >= 1) {
          page.setSelectedCatalogDrafts(
            created.map((variant) => ({
              catalogId: variant.id,
              quantity: "1",
            })),
          );
        }
      });
    } catch (error) {
      runInAction(() => {
        page.actionError = getApiErrorMessage(
          error,
          "Could not create catalog variant(s).",
        );
      });
    } finally {
      runInAction(() => {
        page.creatingCatalog = false;
      });
    }
  };

  const handleUpdateQuantity = async (row: OrderProductsVariantSchema) => {
    if (!canEdit || !order_id || !product_id) return;
    const qty = parseInt(page.editQuantity, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      page.actionError = "Enter a valid quantity (minimum 1).";
      return;
    }
    try {
      page.saving = true;
      page.actionError = null;
      const payload = {
        product_id: Number(product_id),
        sku: row.sku,
        quantity: qty,
      };
      await moduleStore.updateOrderVariant(Number(order_id), row.id, payload);
      await moduleStore.loadOrderVariants(Number(order_id), Number(product_id));
      notifyVariantsCount();
      onSaveVariant?.(
        toParentPayload(
          product_id,
          row.title || null,
          payload.quantity,
          payload.sku,
        ),
      );
      runInAction(() => {
        page.editingOrderVariantId = null;
        page.editQuantity = "";
      });
    } catch (error) {
      runInAction(() => {
        page.actionError = getApiErrorMessage(
          error,
          "Could not update quantity.",
        );
      });
    } finally {
      runInAction(() => {
        page.saving = false;
      });
    }
  };

  const handleDelete = async (row: OrderProductsVariantSchema) => {
    if (!canEdit || !order_id || !product_id) return;
    try {
      page.deletingId = row.id;
      page.actionError = null;
      await moduleStore.deleteOrderVariant(Number(order_id), row.id);
      notifyVariantsCount();
      onDeleteVariant?.(
        toParentPayload(product_id, row.title || null, row.quantity, row.sku),
      );
    } catch (error) {
      runInAction(() => {
        page.actionError = getApiErrorMessage(
          error,
          "Could not delete variant.",
        );
      });
    } finally {
      runInAction(() => {
        page.deletingId = null;
      });
    }
  };

  const loading =
    isLoading ||
    moduleStore.loadingOrderVariants ||
    (page.step === "catalog" && moduleStore.loadingCatalogVariants);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
        style: { zIndex: 1200 + VARIANT_DRAWER_Z_OFFSET },
      }}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 620, md: 720 },
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          backgroundColor: colors.white,
          boxShadow:
            "0 24px 80px rgba(15, 23, 42, 0.25), 0 6px 20px rgba(15, 23, 42, 0.12)",
        },
      }}
    >
      <Box
        sx={{
          px: 2.75,
          py: 2.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: `1px solid ${colors.greyBorder}`,
          backgroundColor: colors.white,
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} minWidth={0}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "8px",
              backgroundColor: "#EEF2FF",
              color: colors.purpleMain,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box minWidth={0}>
            <CustomTypography variant="heading6" fontWeight={700}>
              Product Variants
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={colors.greyTextColor}
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: { xs: "76vw", sm: 480 },
              }}
            >
              {page.productName || "—"}
            </CustomTypography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: colors.greyTextColor,
            "&:hover": { backgroundColor: "#F1F5F9" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Step tabs */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          display: "flex",
          gap: 1,
          borderBottom: `1px solid ${colors.greyBorder}`,
          pb: 1.5,
        }}
      >
        <Chip
          clickable
          label="1 · Order SKUs"
          color={page.step === "order" ? "primary" : "default"}
          variant={page.step === "order" ? "filled" : "outlined"}
          onClick={() => page.setStep("order")}
          sx={{ fontWeight: 600 }}
        />
        <Chip
          clickable
          label="2 · Add from catalog"
          color={page.step === "catalog" ? "primary" : "default"}
          variant={page.step === "catalog" ? "filled" : "outlined"}
          onClick={() => {
            if (!canEdit) return;
            page.setStep("catalog");
            if (
              product_id &&
              moduleStore.catalogVariants.length === 0 &&
              !moduleStore.loadingCatalogVariants
            ) {
              void moduleStore.loadCatalogVariants(Number(product_id));
            }
          }}
          sx={{ fontWeight: 600, opacity: canEdit ? 1 : 0.5 }}
        />
      </Box>

      {loading ? (
        <Box
          flex={1}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, py: 2 }}>
          {(page.bootstrapError || page.actionError) && (
            <Paper
              variant="outlined"
              sx={{
                mb: 2,
                px: 1.5,
                py: 1.25,
                borderColor: "#FECACA",
                backgroundColor: "#FEF2F2",
                borderRadius: 2,
              }}
            >
              <CustomTypography
                variant="body3"
                sx={{ color: colors.errorRed, fontWeight: 500 }}
              >
                {page.actionError || page.bootstrapError}
              </CustomTypography>
            </Paper>
          )}

          {page.step === "order" ? (
            <Box>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
                gap={1}
                flexWrap="wrap"
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <CustomTypography variant="body2" fontWeight={700}>
                    Linked on this order
                  </CustomTypography>
                  <Chip
                    size="small"
                    label={moduleStore.orderVariants.length}
                    sx={{
                      height: 20,
                      fontWeight: 700,
                      backgroundColor: "#EEF2FF",
                      color: colors.purpleMain,
                    }}
                  />
                </Box>
                {canEdit ? (
                  <AppButton
                    label="Add from catalog"
                    variant="secondary"
                    startIcon={<AddIcon />}
                    onClick={() => page.setStep("catalog")}
                    customStyles={{
                      minWidth: 150,
                      minHeight: 36,
                      fontSize: "0.78rem",
                      backgroundColor: "#FDF2F8",
                      color: "#DB2777",
                      border: "1px solid #FBCFE8",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#FCE7F3",
                        boxShadow: "none",
                      },
                    }}
                  />
                ) : null}
              </Box>

              <CustomTypography
                variant="body3"
                color={colors.greyTextColor}
                sx={{ mb: 2 }}
              >
                These SKUs are already saved on this order product. Update
                quantity or remove one, or add another from the product catalog.
              </CustomTypography>

              {moduleStore.orderVariants.length === 0 ? (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  py={6}
                  gap={1}
                >
                  <Inventory2OutlinedIcon
                    sx={{ fontSize: 36, color: colors.greyBorder }}
                  />
                  <CustomTypography
                    variant="body2"
                    color={colors.greyTextColor}
                  >
                    No variants linked yet.
                  </CustomTypography>
                  {canEdit ? (
                    <AppButton
                      label="Add from catalog"
                      variant="primary"
                      onClick={() => page.setStep("catalog")}
                    />
                  ) : null}
                </Box>
              ) : (
                <Grid2 container spacing={1} columns={12}>
                  {moduleStore.orderVariants.map((row) => {
                    const isEditing = page.editingOrderVariantId === row.id;
                    return (
                      <Grid2 key={row.id} size={{ xs: 12 }}>
                        <Paper
                          variant="outlined"
                          sx={{
                            px: 1.25,
                            py: isEditing ? 1 : 0.875,
                            borderRadius: 1.5,
                            borderColor: isEditing
                              ? colors.purpleMain
                              : colors.greyBorder,
                            backgroundColor: isEditing ? "#FAFAFF" : colors.white,
                            transition: "border-color 0.15s, background-color 0.15s",
                          }}
                        >
                          {isEditing ? (
                            <Box display="flex" flexDirection="column" gap={0.75}>
                              <Box
                                display="flex"
                                alignItems="flex-start"
                                justifyContent="space-between"
                                gap={1}
                              >
                                <Box minWidth={0}>
                                  <CustomTypography
                                    variant="body2"
                                    fontWeight={700}
                                    sx={{
                                      fontSize: "0.82rem",
                                      lineHeight: 1.3,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {row.title || "—"}
                                  </CustomTypography>
                                  <SkuCopyRow
                                    sku={row.sku}
                                    typographySx={{
                                      fontSize: "0.72rem",
                                      lineHeight: 1.2,
                                    }}
                                  />
                                </Box>
                              </Box>

                              <Box
                                display="flex"
                                alignItems="center"
                                gap={0.75}
                              >
                                <CustomTypography
                                  variant="caption"
                                  color={colors.greyTextColor}
                                  sx={{ flexShrink: 0, fontSize: "0.72rem" }}
                                >
                                  Qty
                                </CustomTypography>
                                <CustomTextField
                                  width="72px"
                                  type="number"
                                  allowNegativeValue={false}
                                  allowDecimalValue={false}
                                  value={page.editQuantity}
                                  disabled={page.saving}
                                  fontSize="0.78rem"
                                  borderRadius="6px"
                                  onChange={(e) => {
                                    page.editQuantity = e.target.value;
                                  }}
                                />
                                <Box sx={{ flex: 1 }} />
                                <IconButton
                                  size="small"
                                  disabled={page.saving}
                                  onClick={() => {
                                    page.editingOrderVariantId = null;
                                    page.editQuantity = "";
                                  }}
                                  sx={{
                                    color: colors.greyTextColor,
                                    "&:hover": {
                                      backgroundColor: "#F1F5F9",
                                    },
                                  }}
                                >
                                  <CloseIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  disabled={page.saving}
                                  onClick={() => void handleUpdateQuantity(row)}
                                  sx={{
                                    color: colors.purpleMain,
                                    backgroundColor: "#EEF2FF",
                                    "&:hover": {
                                      backgroundColor: "#E0E7FF",
                                    },
                                  }}
                                >
                                  {page.saving ? (
                                    <CircularProgress size={14} />
                                  ) : (
                                    <CheckIcon sx={{ fontSize: 16 }} />
                                  )}
                                </IconButton>
                              </Box>
                            </Box>
                          ) : (
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={1}
                              minHeight={36}
                            >
                              <Box minWidth={0} flex={1}>
                                <CustomTypography
                                  variant="body2"
                                  fontWeight={700}
                                  sx={{
                                    fontSize: "0.82rem",
                                    lineHeight: 1.3,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.title || "—"}
                                </CustomTypography>
                                <SkuCopyRow
                                  sku={row.sku}
                                  typographySx={{
                                    fontSize: "0.72rem",
                                    lineHeight: 1.2,
                                  }}
                                />
                              </Box>

                              <Box
                                sx={{
                                  px: 0.9,
                                  py: 0.2,
                                  borderRadius: "6px",
                                  backgroundColor: "#F1F5F9",
                                  flexShrink: 0,
                                }}
                              >
                                <CustomTypography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 700,
                                    color: colors.greyTextColor,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Qty {row.quantity}
                                </CustomTypography>
                              </Box>

                              {canEdit ? (
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  flexShrink={0}
                                  sx={{ ml: -0.25 }}
                                >
                                  <IconButton
                                    size="small"
                                    disabled={page.deletingId === row.id}
                                    onClick={() => {
                                      page.editingOrderVariantId = row.id;
                                      page.editQuantity = String(row.quantity);
                                      page.actionError = null;
                                    }}
                                    sx={{
                                      color: colors.greyTextColor,
                                      p: 0.5,
                                      "&:hover": {
                                        backgroundColor: "#EEF2FF",
                                        color: colors.purpleMain,
                                      },
                                    }}
                                  >
                                    <EditOutlinedIcon sx={{ fontSize: 17 }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    disabled={page.deletingId === row.id}
                                    onClick={() => void handleDelete(row)}
                                    sx={{
                                      color: colors.greyTextColor,
                                      p: 0.5,
                                      "&:hover": {
                                        backgroundColor: "#FEF2F2",
                                        color: colors.errorRed,
                                      },
                                    }}
                                  >
                                    {page.deletingId === row.id ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                                    )}
                                  </IconButton>
                                </Box>
                              ) : null}
                            </Box>
                          )}
                        </Paper>
                      </Grid2>
                    );
                  })}
                </Grid2>
              )}
            </Box>
          ) : (
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <IconButton
                  size="small"
                  onClick={() => page.setStep("order")}
                  sx={{ border: `1px solid ${colors.greyBorder}` }}
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box>
                  <CustomTypography variant="body2" fontWeight={700}>
                    Add catalog variants
                  </CustomTypography>
                  <CustomTypography
                    variant="caption"
                    color={colors.greyTextColor}
                  >
                    Pick one or more from the list, or create new combinations.
                  </CustomTypography>
                </Box>
              </Box>

              <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                <Chip
                  clickable
                  label="Pick from list"
                  color={page.catalogTab === "list" ? "primary" : "default"}
                  variant={page.catalogTab === "list" ? "filled" : "outlined"}
                  onClick={() => page.setCatalogTab("list")}
                  sx={{ fontWeight: 600 }}
                />
                {canCreateVariantsOrOptions && (
                  <Chip
                    clickable
                    label="Create new"
                    color={page.catalogTab === "create" ? "primary" : "default"}
                    variant={
                      page.catalogTab === "create" ? "filled" : "outlined"
                    }
                    disabled={!page.categoryId || page.creatingCatalog}
                    onClick={() => page.setCatalogTab("create")}
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </Box>

              {page.catalogTab === "list" ? (
                <Box>
                  <Box mb={2} display="flex" alignItems="center" gap={1}>
                    <SearchIcon
                      sx={{ color: colors.greyTextColor, fontSize: 18 }}
                    />
                    <TextField
                      variant="standard"
                      fullWidth
                      placeholder="Search by SKU or title…"
                      value={page.catalogSearch}
                      disabled={page.saving || page.creatingCatalog}
                      onChange={(e) =>
                        page.setCatalogSearch(e.target.value || "")
                      }
                      sx={{
                        "& .MuiInputBase-input": {
                          fontSize: "0.85rem",
                          py: 0.75,
                          "&::placeholder": {
                            color: colors.greyTextColor,
                            opacity: 1,
                          },
                        },
                      }}
                    />
                  </Box>

                  <Box
                    display="flex"
                    flexDirection="column"
                    gap={1}
                    mb={2}
                    maxHeight={320}
                    overflow="auto"
                  >
                    {filteredCatalog.length === 0 ? (
                      <CustomTypography
                        variant="body3"
                        color={colors.greyTextColor}
                      >
                        {debouncedCatalogSearch.trim()
                          ? "No catalog variants match your search."
                          : "No active catalog variants on this product yet. Use Create new."}
                      </CustomTypography>
                    ) : (
                      filteredCatalog.map((v) => {
                        const selected = page.selectedCatalogDrafts.some(
                          (draft) => draft.catalogId === v.id,
                        );
                        const linked = alreadyLinkedSku.has(
                          (v.sku || `CAT-${v.id}`).trim().toLowerCase(),
                        );
                        return (
                          <Paper
                            key={v.id}
                            variant="outlined"
                            onClick={() => {
                              if (linked || page.saving) return;
                              page.toggleCatalogVariant(v.id);
                            }}
                            sx={{
                              px: 1.5,
                              py: 1.25,
                              borderRadius: 2,
                              cursor: linked ? "not-allowed" : "pointer",
                              opacity: linked ? 0.55 : 1,
                              borderColor: selected
                                ? colors.purpleMain
                                : colors.greyBorder,
                              backgroundColor: selected
                                ? "#F5F3FF"
                                : colors.white,
                            }}
                          >
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              gap={1}
                            >
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                minWidth={0}
                              >
                                {v.hex_code ? (
                                  <Box
                                    sx={{
                                      width: 14,
                                      height: 14,
                                      borderRadius: "50%",
                                      bgcolor: v.hex_code,
                                      border: `1px solid ${colors.greyBorder}`,
                                      flexShrink: 0,
                                    }}
                                  />
                                ) : null}
                                <Box minWidth={0}>
                                  <CustomTypography
                                    variant="body2"
                                    fontWeight={600}
                                  >
                                    {catalogLabel(v)}
                                  </CustomTypography>
                                  <SkuCopyRow
                                    sku={v.sku}
                                    emptyFallback={`Variant #${v.id}`}
                                    suffix={linked ? " · already linked" : ""}
                                  />
                                </Box>
                              </Box>
                              {selected ? (
                                <Chip
                                  size="small"
                                  label="Selected"
                                  color="primary"
                                  sx={{ height: 22, fontWeight: 600 }}
                                />
                              ) : null}
                            </Box>
                          </Paper>
                        );
                      })
                    )}
                  </Box>

                  {page.selectedCatalogDrafts.length > 0 ? (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 2,
                        mb: 2,
                        borderRadius: 2,
                        borderColor: colors.purpleMain,
                        backgroundColor: "#F8FAFF",
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={1}
                        mb={1.5}
                      >
                        <CustomTypography variant="body2" fontWeight={700}>
                          Selected variants ({page.selectedCatalogDrafts.length}
                          )
                        </CustomTypography>
                        <AppButton
                          label="Clear all"
                          variant="transparentPrimary"
                          disabled={page.saving}
                          onClick={() => {
                            page.selectedCatalogDrafts = [];
                          }}
                        />
                      </Box>

                      <Box
                        display="flex"
                        flexDirection="column"
                        gap={1.25}
                        mb={2}
                      >
                        {page.selectedCatalogDrafts.map((draft) => {
                          const catalog = moduleStore.catalogVariants.find(
                            (variant) => variant.id === draft.catalogId,
                          );
                          if (!catalog) return null;

                          return (
                            <Paper
                              key={draft.catalogId}
                              variant="outlined"
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                borderColor: colors.greyBorder,
                                backgroundColor: colors.white,
                              }}
                            >
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                gap={1}
                                mb={1.25}
                              >
                                <Box minWidth={0}>
                                  <CustomTypography
                                    variant="body2"
                                    fontWeight={700}
                                  >
                                    {catalogLabel(catalog)}
                                  </CustomTypography>
                                  <SkuCopyRow
                                    sku={catalog.sku}
                                    emptyFallback={`Variant #${catalog.id}`}
                                  />
                                </Box>
                                <IconButton
                                  size="small"
                                  disabled={page.saving}
                                  onClick={() =>
                                    page.removeSelectedCatalog(draft.catalogId)
                                  }
                                  sx={{ color: colors.greyTextColor }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              </Box>

                              <CustomTypography
                                variant="caption"
                                color={colors.greyTextColor}
                                sx={{ mb: 0.5, display: "block" }}
                              >
                                Quantity
                              </CustomTypography>
                              <CustomTextField
                                width="100%"
                                type="number"
                                allowNegativeValue={false}
                                allowDecimalValue={false}
                                value={draft.quantity}
                                disabled={page.saving}
                                onChange={(e) => {
                                  page.setSelectedCatalogQuantity(
                                    draft.catalogId,
                                    e.target.value,
                                  );
                                }}
                              />
                            </Paper>
                          );
                        })}
                      </Box>

                      <AppButton
                        label={
                          page.saving
                            ? "Saving…"
                            : `Save ${page.selectedCatalogDrafts.length} variant${
                                page.selectedCatalogDrafts.length === 1
                                  ? ""
                                  : "s"
                              } to order product`
                        }
                        variant="primary"
                        disabled={page.saving}
                        onClick={() => void handleSaveSelectedCatalog()}
                        customStyles={{ width: "100%" }}
                      />
                    </Paper>
                  ) : null}
                </Box>
              ) : (
                <Box>
                  {!page.categoryId ? (
                    <CustomTypography
                      variant="body3"
                      color={colors.greyTextColor}
                    >
                      This product has no primary category, so new variants
                      cannot be built here.
                    </CustomTypography>
                  ) : (
                    <Box>
                      <CustomTypography
                        variant="body3"
                        color={colors.greyTextColor}
                        sx={{ mb: 2 }}
                      >
                        Build new catalog combinations. After create, we&apos;ll
                        open the list with them selected so you can set
                        quantities.
                      </CustomTypography>
                      <ProductSkuBuilder
                        key={page.skuBuilderResetKey}
                        categoryId={page.categoryId}
                        disabled={page.creatingCatalog || page.saving}
                        serverSku={page.productSku}
                        resetKey={page.skuBuilderResetKey}
                        onVariantsChange={(variants) => {
                          page.pendingNewVariants = variants;
                        }}
                      />
                      {page.pendingNewVariants.length > 0 ? (
                        <Box display="flex" justifyContent="flex-end" mt={2}>
                          <AppButton
                            label={
                              page.creatingCatalog
                                ? "Creating…"
                                : `Create ${page.pendingNewVariants.length} variant${
                                    page.pendingNewVariants.length === 1
                                      ? ""
                                      : "s"
                                  } & add to selection`
                            }
                            variant="primary"
                            disabled={page.creatingCatalog}
                            onClick={() => void handleCreateCatalogVariants()}
                          />
                        </Box>
                      ) : null}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}
    </Drawer>
  );
};

export default observer(OrderProductVariantsDrawer);
